import axios from '@/lib/axios';

export interface ProgressPoint {
    date: string;
    questionsAnswered: number;
    correctCount: number;
    accuracy: number;
}

export interface SkillPerformance {
    skillName: string;
    skillCode: string;
    questionsAnswered: number;
    accuracy: number;
    domainName: string;
}

export interface TimelineEvent {
    id: string;
    type: "PRACTICE" | "QUIZ" | "MOCK_TEST" | "READING";
    title: string;
    timestamp: string;
    durationSeconds: number;
    questionsAnswered: number;
    correctCount: number;
    incorrectCount: number;
    score?: number;
    metadata?: Record<string, any>;
}

export interface DailyTimelineResponse {
    date: string;
    events: TimelineEvent[];
}

export interface DomainPerformance {
    domainName: string;
    questionsAnswered: number;
    accuracy: number;
}

export interface ScoreBreakdown {
    baseScore: number;
    accuracyScore: number;
    volumeBonus: number;
    totalScore: number;
    mathScore: number;
    englishScore: number;
}

export interface PerformanceOverview {
    totalQuestionsAnswered: number;
    overallAccuracy: number;
    totalTimeSpentMinutes: number;
    questionsThisWeek: number;
    currentStreak: number;
    projectedScore: number;
    projectedMath?: number;
    projectedRW?: number;
    scoreBreakdown?: ScoreBreakdown;
}

export interface PerformanceResponse {
    overview: PerformanceOverview;
    last30DaysProgress: ProgressPoint[];
    skillBreakdown: SkillPerformance[];
    domainBreakdown: DomainPerformance[];
}

export interface LeaderboardEntry {
    rank: number;
    userId: number;
    fullName: string;
    avatarUrl?: string;
    questionsSolved: number;
    successRate: number;
    totalAttempts: number;
    score: number;
    isCurrentUser: boolean;
    membershipName?: string;
}



export interface LeaderboardResponse {
    period: string;
    topPerformers: LeaderboardEntry[];
    userRank?: LeaderboardEntry;
    leaderboard: LeaderboardEntry[];
}

export interface ApiStats {
    total_requests: number
    avg_duration_ms: number
    top_users: {
        user_id: number
        name: string
        email: string
        cnt: number
    }[]
    top_endpoints: {
        path: string
        count: number
    }[]
    top_ai_users: {
        user_id: number
        name: string
        email: string
        cnt: number
    }[]
}

export interface ApiLog {
    id: number | string
    user_id: number | null
    path: string
    method: string
    status_code: number
    duration_ms: number
    ip_address: string | null
    timestamp: string
}

export interface MockTestLeaderboardEntry {
    rank: number;
    userId: number;
    fullName: string;
    avatarUrl?: string;
    totalScore: number;
    rwScore: number;
    mathScore: number;
    completedAt: string;
}

export interface MockTestLeaderboardResponse {
    topPerformers: MockTestLeaderboardEntry[];
}

export const analyticsService = {
    getPerformanceStats: async (days: number = 30): Promise<PerformanceResponse> => {
        const response = await axios.get('/analytics/performance', { params: { days } });
        return response.data;
    },

    getLeaderboard: async (period: string = 'weekly'): Promise<LeaderboardResponse> => {
        const response = await axios.get('/analytics/leaderboard', { params: { period } });
        return response.data;
    },

    getMockTestLeaderboard: async (): Promise<MockTestLeaderboardResponse> => {
        const response = await axios.get('/analytics/mock-tests-leaderboard');
        return response.data;
    },

    getApiUsageStats: async (): Promise<ApiStats> => {
        const response = await axios.get('/analytics/usage/stats');
        return response.data;
    },

    getApiUsageLogs: async (limit = 50, offset = 0, userId?: number): Promise<ApiLog[]> => {
        const params = { limit, offset, user_id: userId };
        const response = await axios.get('/analytics/usage/logs', { params });
        return response.data;
    },

    getDailyTimeline: async (date: string): Promise<DailyTimelineResponse> => {
        const response = await axios.get('/analytics/daily-timeline', { params: { date_str: date } });
        return response.data;
    }
};
